'use client';

import { useSyncExternalStore } from 'react';
import { RECENT_LIMIT, RECENT_SEARCHES_KEY } from './constants';
import type { RecentSearch } from './types';

/**
 * Recent searches, newest first, kept in this browser (13.10): the queries
 * the player committed (Enter, "See all") and the results they opened. One
 * list for every kind, as the chips under "Recent searches" show them.
 */

const EMPTY: RecentSearch[] = [];
const listeners = new Set<() => void>();
let cache: RecentSearch[] | null = null;

function isRecentSearch(value: unknown): value is RecentSearch {
  const item = value as RecentSearch | null;
  return typeof item?.id === 'string' && typeof item.label === 'string';
}

function read(): RecentSearch[] {
  if (cache) return cache;
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(RECENT_SEARCHES_KEY) ?? '[]');
    cache = Array.isArray(parsed) ? parsed.filter(isRecentSearch).slice(0, RECENT_LIMIT) : EMPTY;
  } catch {
    cache = EMPTY;
  }
  return cache;
}

function write(items: RecentSearch[]) {
  cache = items.slice(0, RECENT_LIMIT);
  try {
    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(cache));
  } catch {
    // A full or blocked storage keeps the list for this visit only.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getServerSnapshot = () => EMPTY;

export const recentSearches = {
  push(item: RecentSearch) {
    write([item, ...read().filter((r) => r.id !== item.id)]);
  },
  remove(id: string) {
    write(read().filter((r) => r.id !== id));
  },
  clear() {
    write([]);
  },
};

/** A typed query, as a chip. */
export function rememberQuery(query: string) {
  const label = query.trim();
  if (label) recentSearches.push({ id: `query:${label.toLowerCase()}`, kind: 'query', label });
}

export function useRecentSearches(): RecentSearch[] {
  return useSyncExternalStore(subscribe, read, getServerSnapshot);
}
